import { A11yModule } from '@angular/cdk/a11y';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { heroiconsAnnotation } from '@ld.consumer-portal/data-uri';
import { SvgIconComponent } from '@ld.consumer-portal/ui-shared';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

export interface PhoneAuthorizationDialogData {
  mode: 'authorize' | 'revoke';
  phoneNumber: string;
}

export type PhoneAuthorizationDialogResult = { action: 'confirm' } | { action: 'cancel' };

@Component({
  selector: 'ld-phone-authorization-dialog',
  template: `
    <ng-container *transloco="let t">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-ld-border">
          <h2 class="text-xl font-poppins font-medium text-ld-neutral m-0">{{ title }}</h2>
        </div>

        <!-- Content -->
        <div class="p-6">
          <div class="flex justify-center mb-4">
            <ld-svg-icon [src]="annotationIcon" class="w-6 h-6" aria-hidden="true" />
          </div>
          <p class="font-dm text-sm text-ld-neutral-500 text-justify mb-4">{{ message }}</p>
        </div>

        <!-- Footer -->
        <div class="px-6 py-6 border-t-0 flex flex-row items-center justify-end gap-4">
          <button
            type="button"
            class="text-ld-neutral-500 font-dm font-medium text-sm cursor-pointer bg-transparent border-0 hover:text-ld-neutral-700 transition-colors"
            [disabled]="isLoading()"
            (click)="onCancel()"
          >
            {{ t('commonButtonsCancel') }}
          </button>
          <button
            type="button"
            cdkFocusInitial
            [class]="data.mode === 'authorize' ? 'btn-primary-ld-purple' : 'btn-solid-red'"
            [disabled]="isLoading()"
            (click)="onConfirm()"
          >
            @if (isLoading()) {
              <mat-spinner diameter="16" class="w-4! h-4!"></mat-spinner>
            }
            {{ confirmText }}
          </button>
        </div>
      </div>
    </ng-container>
  `,
  imports: [A11yModule, MatProgressSpinnerModule, SvgIconComponent, TranslocoDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneAuthorizationDialogComponent {
  private readonly dialogRef = inject<DialogRef<PhoneAuthorizationDialogResult>>(DialogRef);
  protected readonly data = inject<PhoneAuthorizationDialogData>(DIALOG_DATA);
  private readonly transloco = inject(TranslocoService);

  protected readonly annotationIcon = heroiconsAnnotation;
  protected readonly isLoading = signal(false);

  protected get formattedPhone(): string {
    const num = this.data.phoneNumber.replace(/\D/g, '');
    if (num.length === 10) {
      return `(${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6)}`;
    }
    return this.data.phoneNumber;
  }

  protected get title(): string {
    return this.data.mode === 'authorize'
      ? this.transloco.translate('phoneAuthModalTitleAuthorize')
      : this.transloco.translate('phoneAuthModalTitleRevoke');
  }

  protected get message(): string {
    if (this.data.mode === 'authorize') {
      return this.transloco.translate('phoneAuthModalMessageAuthorize');
    }
    return this.transloco.translate('phoneAuthModalMessageRevoke', { phone: this.formattedPhone });
  }

  protected get confirmText(): string {
    return this.data.mode === 'authorize'
      ? this.transloco.translate('phoneAuthModalButtonAuthorize')
      : this.transloco.translate('phoneAuthModalButtonRevoke');
  }


  protected onConfirm(): void {
    this.dialogRef.close({ action: 'confirm' });
  }

  protected onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }
}
